/* eslint-disable react/iframe-missing-sandbox */
import React, { useCallback, useLayoutEffect } from 'react';
import { Platform } from 'react-native';
import {
  Modal,
  TransparentModal,
  useResponsive,
  useSafeExpoNavigation,
  useSafeNavigation,
  useSafeRouteParams,
  useSafeRouter,
} from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import type { PersonalDataDetailsScreenProps } from '../../navigation/AppNavigation.props';

export const PersonalDataDetailsScreen = (
  params: PersonalDataDetailsScreenProps,
) => {
  const { navigation: navProp, route } = params;

  const { match, currentBreakpoint } = useResponsive();
  const { t } = useLocalizationContext();

  const fallbackNavigation = useSafeNavigation();
  const expoNavigation = useSafeExpoNavigation();
  const router = useSafeRouter();
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const routeParams = useSafeRouteParams<{ action?: string }>();

  // Personal data request can be for access or deletion.
  const isAccessRequest =
    (route?.params?.action ?? routeParams?.action) === 'access';

  // Dismissing modal just goes back on navigation.
  const handleDismiss = useCallback(() => {
    if (isExpoRouterEnabled) {
      if (router?.canGoBack()) {
        router.back();
        return;
      }

      router?.navigate('/account/info');

      return;
    }

    const navigation = navProp ?? fallbackNavigation;

    if (navigation?.canGoBack()) {
      navigation.goBack();
      return;
    }

    navigation?.getParent?.()?.goBack();
  }, [isExpoRouterEnabled, router, navProp, fallbackNavigation]);

  // Dynamic title based on received navigation parameter.
  const headerTitle = t(
    isAccessRequest ? 'personal-data.access-cta' : 'personal-data.delete-cta',
  );

  // Mobile title.
  const setOptions =
    navProp?.setOptions ??
    expoNavigation?.setOptions ??
    fallbackNavigation?.setOptions;

  useLayoutEffect(() => {
    setOptions?.({ headerTitle });
  }, [setOptions, headerTitle]);

  // Dynamic iFrame URL based on received navigation parameter.
  const oneTrustUrl = isAccessRequest
    ? ONE_TRUST_ACCESS_URL
    : ONE_TRUST_DELETE_URL;

  // This is just in case, it is not possible to navigate here directly.
  if (Platform.OS !== 'web') return null; // disabled on native platforms.

  return (
    <TransparentModal size={match(['full', 'large'])} onClose={handleDismiss}>
      {currentBreakpoint.isXS ? null : (
        <Modal.Header onClose={handleDismiss}>{headerTitle}</Modal.Header>
      )}
      <iframe
        src={oneTrustUrl}
        style={match([frameStyleSM, frameStyleLG])}
        id="one-trust-ca"
        title="one-trust-ca"
      />
    </TransparentModal>
  );
};

// ─── Constants ──────────────────────────────────────────────────────
const ONE_TRUST_ACCESS_URL =
  'https://privacyportal.onetrust.com/webform/21a1d4f5-76a6-4640-87ad-0e9b616c1963/61a7235b-359e-4316-8c79-03620685bd69';
const ONE_TRUST_DELETE_URL =
  'https://privacyportal.onetrust.com/webform/21a1d4f5-76a6-4640-87ad-0e9b616c1963/b9ce941b-7bf5-4377-8ef0-ddb32fa17a89';

// ─── Styles ─────────────────────────────────────────────────────────
const frameStyleSM = { border: 0, height: '100%' };
const frameStyleLG = { border: 0, height: 460, maxHeight: '100%' };
